import type { BundleOutputFile, PackageJsonExports } from '@ms-cloudpack/common-types';
import { getRuntimeEntryPaths } from '@ms-cloudpack/import-map';
import { normalizedPathRelativeTo } from '@ms-cloudpack/path-utilities';
import path from 'path';
import type { BundleEntryInfo, BundleInfo } from '../types/BundleInfo.js';
import { getImportsAndExports } from './getImportsAndExports.js';

/**
 * Returns a mapping from entry file path (exports map key) to list of paths that are produced
 * and consumed in that file.
 */
export async function getBundleInfo(params: {
  packagePath: string;
  outputPath: string;
  outputFiles: BundleOutputFile[];
  exportsMap: PackageJsonExports;
  isExternal: boolean;
}): Promise<BundleInfo> {
  const { packagePath, outputPath, outputFiles, exportsMap, isExternal } = params;
  const bundleInfo: BundleInfo = {};

  // If there are no output files, return the empty bundle info.
  if (!outputFiles.length) {
    return bundleInfo;
  }

  /** Mapping from normalized relative output file path to info about which entry paths
   * (exports map keys) pointed to that file, and what export names it produces.
   * Usually there's at most one entry path per output file. */
  const outputFileInfo = Object.fromEntries(
    outputFiles.map((file) => [
      file.outputPath,
      { exportsKeys: [] as string[], produces: new Set<string>(file.exports) },
    ]),
  );

  // For each entry path (exports map key), get the normalized relative path to the output file
  // that will be used at runtime based on conditions.
  const entryToOutputFile = getRuntimeEntryPaths({ packagePath, isExternal, exportsMap });

  // Update the output file info with the entry paths (exports keys) that point to each output file.
  // Usually there's at most one per output file (or none if it's a shared chunk).
  for (const [entryPath, relOutputFile] of Object.entries(entryToOutputFile)) {
    outputFileInfo[relOutputFile]?.exportsKeys.push(entryPath);
  }

  // Build the bundle info based on imports and exports for each output file.
  // exportsKeys are the export map key(s) that point to the output file.
  for (const [relOutputFile, { exportsKeys, produces }] of Object.entries(outputFileInfo)) {
    // Ignore any non-JS/TS files such as SVGs and sourcemaps
    if (path.extname(relOutputFile) !== '.js') {
      continue;
    }

    const absOutputFile = path.resolve(outputPath, relOutputFile);

    const parseResult = await getImportsAndExports(absOutputFile, packagePath);
    if (!parseResult) {
      continue;
    }
    const { exportNames, imports, dynamicImports } = parseResult;

    exportNames.forEach((name) => produces.add(name));

    // Create a bundle info entry for this output file.
    // If there wasn't an exports map key pointing to this file, just use the output path.
    const pathsInBundleInfo = exportsKeys.length ? exportsKeys : [relOutputFile];
    const fileBundleInfo: BundleEntryInfo = bundleInfo[pathsInBundleInfo[0]] || {
      bundlePath: relOutputFile,
      produces: Array.from(produces),
      consumes: [],
    };
    // Use the same object for all paths that point to this file.
    pathsInBundleInfo.forEach((p) => (bundleInfo[p] = fileBundleInfo));

    if (dynamicImports.length) {
      fileBundleInfo.dynamicImports = dynamicImports;
    }

    for (const { packageName, importPath, names } of imports) {
      if (packageName === '.') {
        const localPath = normalizedPathRelativeTo(outputPath, path.resolve(path.dirname(absOutputFile), importPath));
        // Pretty sure any path in the current package which is imported in the bundle output should be
        // in outputFiles, but make it obvious if not.
        if (!outputFileInfo[localPath]) {
          throw new Error(
            `Imported local file not found in output files for ${outputPath}: ${localPath} ` +
              `(imported from ${absOutputFile} with import path "${importPath}")`,
          );
        }
      } else if (
        !packageName.startsWith('node:') &&
        !packageName.startsWith('data:') /* Ignore data URLs as they are not externalized by the bundlers. */
      ) {
        // Find if the imported package is already in the consumes list.
        const consumesFromPackage = fileBundleInfo.consumes.find(
          (c) => c.packageName === packageName && c.importPath === importPath,
        );
        if (consumesFromPackage) {
          // If so, add the names to the existing entry.
          // This is necessary because the same import may be used in multiple places.
          consumesFromPackage.names = Array.from(new Set([...names, ...consumesFromPackage.names]));
        } else {
          // Otherwise, add it.
          fileBundleInfo.consumes.push({
            packageName: packageName,
            importPath: importPath || '.',
            names: [...names],
          });
        }
      }
    }
  }

  return bundleInfo;
}
